import type { FilterType } from '@/domain/prices/filters/types'
import type { AlternativeFilters, LegendItem, Price, PricesResponse } from '@/domain/prices/prices/types'
import type {
  AlternateFacets,
  APIMessage,
  APIPrice,
  Data,
  PricesByDurationRawResponse,
} from '@/infrastructure/prices/pricesByDuration/api/types'
import { mapMonthValue } from '@/domain/prices/utils'
import { tk } from '@sunwebgroup/ui-components'

export interface PricesResponseMapper {
  map: (pricesAPIResponse: PricesByDurationRawResponse) => PricesResponse
}

export function createPricesResponseMapper() {
  const map = (pricesAPIResponse: PricesByDurationRawResponse) => {
    const prices: Price[] = []
    const datesSet = new Set<string>()
    const bestValueThreshold = pricesAPIResponse.data.bestValues?.[0]?.threshold
    const highlightInfo = getHighlightInfo(pricesAPIResponse.data)

    Object.values(pricesAPIResponse.data.prices ?? {}).forEach((price) => {
      const newPrice = mapPriceToInfo(price, highlightInfo, bestValueThreshold)
      prices.push(newPrice)
      datesSet.add(price.departureDate)
    })

    const pricesByDuration = getPricesByDuration(prices ?? [])
    const pricesByDurationAndDate = getPricesByDurationAndDate(pricesByDuration)
    const year = getYearFromPrice(prices[0])
    const messages = mapMessages(pricesAPIResponse.data.messages ?? [])
    const dates = Array.from(datesSet).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    const alternativeFilters = mapAlternativeFilter(pricesAPIResponse.data.alternateFacets)

    return {
      prices,
      pricesByDuration,
      pricesByDurationAndDate,
      dates,
      year,
      bestValueThreshold,
      highlightInfo,
      messages,
      alternativeFilters,
    }
  }

  return {
    map,
  }
}

function mapPriceToInfo(price: APIPrice, highlightInfo: LegendItem[], bestValueThreshold?: number): Price {
  const key = `${price.duration}|${price.departureDate}`
  const bestValueScore = price.bestValueScore ?? 0

  return {
    id: key,
    minPricePerPerson: price.minPricePerPerson,
    departureDate: price.departureDate,
    duration: price.duration,
    mealplan: price.mealplan,
    bullet: highlightInfo.find(legendItem => legendItem.code === price.highlightCode) ?? null,
    isBestValue: Boolean(bestValueThreshold && bestValueScore >= bestValueThreshold),
    isFlightOnly: price.isFlightOnly ?? false,
  }
}

function getPricesByDuration(prices: Price[]): Record<number, Price[]> {
  return prices.reduce<Record<number, Price[]>>((acc, price) => {
    if (!acc[price.duration]) {
      acc[price.duration] = []
    }
    acc[price.duration].push(price)
    return acc
  }, {})
}

function getPricesByDurationAndDate(pricesByDuration: Record<number, Price[]>): Record<number, Record<string, Price>> {
  return Object.entries(pricesByDuration).reduce<Record<number, Record<string, Price>>>(
    (acc, [duration, pricesForDuration]) => {
      acc[Number(duration)] = pricesForDuration.reduce<Record<string, Price>>(
        (innerAcc: Record<string, Price>, price) => {
          innerAcc[price.departureDate] = price
          return innerAcc
        },
        {},
      )
      return acc
    },
    {},
  )
}

function getHighlightInfo(data: Data): LegendItem[] {
  const highlightInfo: LegendItem[] = [...(data.highlightInfo ?? [])]

  if (data.bestValuesCaption) {
    highlightInfo.push({
      code: data.bestValuesCaption.title,
      description: data.bestValuesCaption.title,
      hexColor: tk('color_semantic_border_success'),
      isBestValue: true,
    })
  }

  return highlightInfo
}

function getYearFromPrice(price: APIPrice) {
  if (!price)
    return ''

  return new Date(price.departureDate).getFullYear().toString()
}

function mapMessages(messages: APIMessage[]): APIMessage[] {
  const filteredMessages = messages.filter(message => message.texts && message.texts.some(text => text !== ''))
  return filteredMessages.map((message) => {
    message.texts = message.texts.filter(text => text !== '')
    return message
  })
}

const filterKeyMap: Record<string, keyof FilterType> = {
  durations: 'Duration',
  months: 'Month',
  roomTypes: 'RoomType',
  departureAirports: 'DepartureAirport',
  mealplans: 'Mealplan',
  transportTypes: 'TransportType',
}
function mapAlternativeFilter(facets: AlternateFacets | undefined): AlternativeFilters | null {
  if (!facets)
    return null

  const result = Object.keys(facets).reduce<AlternativeFilters>((acc, key) => {
    if (facets[key].options.length === 0)
      return acc
    const newKey = filterKeyMap[key]
    acc[newKey] = {
      title: facets[key].description,
      options: facets[key].options.map(option => ({
        text: option.caption,
        value: newKey === 'Month' ? mapMonthValue(option.value) : option.value,
      })),
    }

    return acc
  }, {})

  return Object.keys(result).length ? result : null
}
